import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import type {
  Address,
  HouseholdDetail,
  HouseholdInput,
} from "@guestbook/shared";
import type { ApiClientError } from "@/shared/lib/api";
import { Button } from "@/shared/components/ui/Button";
import { FormField } from "@/shared/components/form/FormField";
import { AddressFields } from "@/shared/components/form/AddressFields";

const EMPTY_ADDRESS: Address = {
  line1: null,
  line2: null,
  city: null,
  postalCode: null,
  countryCode: null,
  countryName: null,
  stateCode: null,
  stateName: null,
};

interface HouseholdFormState {
  displayNameCustom: string;
  address: Address;
}

function emptyState(): HouseholdFormState {
  return {
    displayNameCustom: "",
    address: { ...EMPTY_ADDRESS },
  };
}

function stateFromHousehold(hh: HouseholdDetail): HouseholdFormState {
  return {
    displayNameCustom: hh.displayNameCustom ?? "",
    address: hh.address ?? { ...EMPTY_ADDRESS },
  };
}

export interface HouseholdFormProps {
  mode: "create" | "edit";
  initialHousehold?: HouseholdDetail;
  submitLabel: string;
  pending: boolean;
  error: ApiClientError | null;
  onSubmit: (input: HouseholdInput) => void;
}

/**
 * Shared household form for create and edit routes. Membership management
 * is intentionally NOT here — members are wired through the household
 * detail page so the create flow stays a single round-trip.
 */
export function HouseholdForm({
  mode,
  initialHousehold,
  submitLabel,
  pending,
  error,
  onSubmit,
}: HouseholdFormProps) {
  const navigate = useNavigate();
  const [state, setState] = useState<HouseholdFormState>(() =>
    initialHousehold ? stateFromHousehold(initialHousehold) : emptyState(),
  );

  function patch<K extends keyof HouseholdFormState>(key: K, value: HouseholdFormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const input: HouseholdInput = {
      displayNameCustom: state.displayNameCustom.trim() || null,
      address: state.address,
    };
    onSubmit(input);
  }

  return (
    <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
      <section className="flex flex-col gap-6">
        <h2 className="type-heading-sm text-text">Name</h2>
        <p className="type-body-sm text-text-muted mx-4">
          Used when addressing invitations and exports. A household member's
          exported Name and address come from the household when set.
        </p>
        <FormField
          label="Name"
          value={state.displayNameCustom}
          onChange={(e) => patch("displayNameCustom", e.target.value)}
          hint='e.g. "The Smiths" or "Alex & Sam"'
        />
      </section>

      <AddressFields
        value={state.address}
        onChange={(next) => patch("address", next)}
      />

      {error ? (
        <p className="type-body-sm text-danger-strong mx-4">{error.message}</p>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending}>
          {pending
            ? mode === "create"
              ? "Creating…"
              : "Saving…"
            : submitLabel}
        </Button>
      </div>
    </form>
  );
}
